import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Layers } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

const DIMS = [["zone", "Zone"], ["sales_branch", "Sales Branch"], ["vertical", "Vertical"], ["product_category", "Product Category"], ["employee", "Employee"]];
const cr = (v) => (v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// Booking & billing bifurcated by Milestone's own columns (Zone / Sales Branch / Vertical / Product Category)
export function MilestoneBreakdown({ fy, month, filters }) {
  const [groupBy, setGroupBy] = useState("zone");
  const { data, isLoading } = useQuery({
    queryKey: ["bi-breakdown", fy, month, groupBy, filters],
    queryFn: async () => (await api.get("/sales-bi/milestone-breakdown", { params: { fy, month, group_by: groupBy, ...filters } })).data,
  });
  const rows = data?.rows || [];
  const max = Math.max(1, ...rows.map((r) => Math.max(r.booking, r.billing)));
  const cov = data?.coverage?.zone ?? 0;
  return (
    <Card className="p-4 mb-4" data-testid="bi-milestone-breakdown">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" /> Milestone bifurcation · {month === "all" ? "All months" : month} · ₹ Cr</p>
        <div className="flex gap-1 ml-auto" data-testid="bi-breakdown-groupby">
          {DIMS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setGroupBy(k)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${groupBy === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`bi-breakdown-${k}`}>{label}</button>
          ))}
        </div>
      </div>
      {isLoading ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : !rows.length ? (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="bi-breakdown-empty">No Milestone rows for this selection.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="text-left py-1.5 font-semibold">{DIMS.find(([k]) => k === groupBy)?.[1]}</th><th className="text-right py-1.5 font-semibold w-28">Booking</th><th className="text-right py-1.5 font-semibold w-28">Billing</th><th className="py-1.5 w-[40%]"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-slate-50" data-testid={`bi-breakdown-row-${r.key}`}>
                  <td className="py-1.5 text-slate-800 font-medium">{r.key}<span className="text-[11px] text-slate-400 font-normal ml-1.5">{r.booking_rows + r.billing_rows} rows</span></td>
                  <td className="py-1.5 text-right tabular-nums">{cr(r.booking)}</td>
                  <td className="py-1.5 text-right tabular-nums">{cr(r.billing)}</td>
                  <td className="py-1.5 pl-3"><div className="space-y-0.5">
                    <div className="h-1.5 rounded bg-brand/80" style={{ width: `${(r.booking / max) * 100}%` }} />
                    <div className="h-1.5 rounded bg-slate-800/70" style={{ width: `${(r.billing / max) * 100}%` }} />
                  </div></td>
                </tr>
              ))}
              <tr className="font-semibold text-slate-900"><td className="py-1.5">Total</td><td className="py-1.5 text-right tabular-nums">{cr(data.totals.booking)}</td><td className="py-1.5 text-right tabular-nums">{cr(data.totals.billing)}</td><td className="text-[11px] font-normal text-slate-400 pl-3"><span className="inline-block w-2 h-2 rounded-sm bg-brand/80 mr-1" />Booking <span className="inline-block w-2 h-2 rounded-sm bg-slate-800/70 ml-2 mr-1" />Billing</td></tr>
            </tbody>
          </table>
          {cov < 100 && <p className="text-[11px] text-amber-700 mt-2" data-testid="bi-breakdown-coverage">Zone / Vertical / Product Category are present on {cov}% of booking rows — Milestone started sending these columns in Oct 2026; older rows appear as "Not specified".</p>}
        </div>
      )}
    </Card>
  );
}
